import { IssueLiabilityDoc, IssueLiabilityStatus, issueLiabilityStatusMap } from '@gooduncles/gu-app-schema';
import { DatePicker, Select, Spin } from 'antd';
import dayjs from 'dayjs';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { map } from 'rxjs';

import { dayjsesToStringDateWhere, getDefaultDates, getDisableFutureDate } from 'src/utils/date-util';
import { observeIssueLiabilities } from 'src/utils/firebase/firebase-short-cut';

import IssueLiabilityDetailModal from '../IssueManage/IssueLiabilityDetailModal';
import IssueLiabilityDashboard from './IssueLiabilityDashboard';
import IssueLiabilityTable from './IssueLiabilityTable';

const { RangePicker } = DatePicker;

// 기본값: 최근 15일
const defaultDates = getDefaultDates(15, 0);

/**
 * 상태 필터 옵션
 */
const statusOptions = Object.entries(issueLiabilityStatusMap).map(([value, label]) => ({
  value: value as IssueLiabilityStatus,
  label,
}));

/**
 * 손실 책임 관리 페이지
 * - 날짜 범위 선택
 * - 상태 필터
 * - AG-Grid 테이블
 * - 상세보기 모달
 */
const IssueLiabilityManagement: FC = () => {
  // 대시보드용 월 상태
  const [dashboardMonth, setDashboardMonth] = useState<dayjs.Dayjs>(dayjs());

  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>(defaultDates);
  const [selectedStatuses, setSelectedStatuses] = useState<IssueLiabilityStatus[]>([]);
  const [rowData, setRowData] = useState<IssueLiabilityDoc[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLiability, setSelectedLiability] = useState<IssueLiabilityDoc | null>(null);

  const [startDate, endDate] = useMemo(
    () => (dateRange[0] && dateRange[1] ? dayjsesToStringDateWhere(dateRange[0], dateRange[1]) : ['', '']),
    [dateRange],
  );

  // 데이터 구독
  useEffect(() => {
    if (!startDate || !endDate) return;

    setIsLoading(true);

    const subscription = observeIssueLiabilities(
      [
        ['registeredAt', '>=', startDate],
        ['registeredAt', '<=', endDate],
      ],
      {
        sortKey: 'registeredAt',
        orderBy: 'desc',
      },
    )
      .pipe(map((docs) => docs.filter((doc) => doc.deletedAt == null)))
      .subscribe({
        next: (docs) => {
          setRowData(docs);
          setIsLoading(false);
        },
        error: (error) => {
          console.error('Failed to fetch issue liabilities:', error);
          setIsLoading(false);
        },
      });

    return () => subscription.unsubscribe();
  }, [startDate, endDate]);

  // 상태 필터 적용
  const filteredRowData = useMemo(() => {
    if (selectedStatuses.length === 0) return rowData;
    return rowData.filter((item) => selectedStatuses.includes(item.status));
  }, [rowData, selectedStatuses]);

  const handleViewDetail = useCallback((liability: IssueLiabilityDoc) => {
    setSelectedLiability(liability);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedLiability(null);
  }, []);

  return (
    <div className='height100 flexColumn' style={{ gap: 24 }}>
      {/* 대시보드 섹션 */}
      <IssueLiabilityDashboard selectedMonth={dashboardMonth} onMonthChange={setDashboardMonth} />

      {/* 테이블 영역 */}
      <div style={{ flex: 1, minHeight: 0 }}>
        {isLoading ? (
          <div className='height100 flexRow flexCenter'>
            <Spin size='large' />
          </div>
        ) : (
          <IssueLiabilityTable
            rowData={filteredRowData}
            onViewDetail={handleViewDetail}
            toolbarLeftContent={
              <div className='flexRow flexAlignCenter' style={{ gap: 16 }}>
                <div className='flexRow flexAlignCenter' style={{ gap: 8 }}>
                  <span>조회 기간:</span>
                  <RangePicker
                    value={dateRange}
                    onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
                    disabledDate={getDisableFutureDate()}
                    allowClear={false}
                  />
                </div>
                <div className='flexRow flexAlignCenter' style={{ gap: 8 }}>
                  <span>상태:</span>
                  <Select
                    mode='multiple'
                    placeholder='전체'
                    style={{ minWidth: 200 }}
                    value={selectedStatuses}
                    onChange={setSelectedStatuses}
                    options={statusOptions}
                    allowClear
                  />
                </div>
              </div>
            }
          />
        )}
      </div>

      {/* 상세보기 모달 */}
      {selectedLiability && (
        <IssueLiabilityDetailModal open={modalOpen} onClose={handleCloseModal} liability={selectedLiability} />
      )}
    </div>
  );
};

export default IssueLiabilityManagement;
